// Gives every form control and icon-only button an accessible name, so screen readers don't just say "button"
// or "edit text". Most of the app writes <label>Text</label><input/> without linking them and uses bare icons
// for Edit / Delete / Close; rather than hand-edit ~150 places, this links them as they appear on screen.
//   1. <label> with no `for` and no control inside -> linked to the control that follows it (or the only one
//      in its parent)
//   2. a control still without a name -> the nearest label / caption text before it, else its title
//   3. an icon-only button -> its title, else a readable name made from its data-testid ("staff-edit-3f2a.." ->
//      "Edit staff")
const CONTROLS = "input:not([type=hidden]):not([type=checkbox]):not([type=radio]), select, textarea";
const ACTIONS = ["edit", "delete", "remove", "close", "cancel", "print", "save", "add", "send", "archive", "download", "refresh", "next", "prev", "previous", "back", "open", "clear", "reset", "copy", "undo", "toggle", "expand", "collapse", "view", "export", "upload", "confirm", "void"];
let uid = 0;

const hasName = (el) => !!(el.getAttribute("aria-label") || el.getAttribute("aria-labelledby") || el.getAttribute("title") || (el.labels && el.labels.length));
const visibleText = (el) => (el.textContent || "").replace(/\s+/g, " ").trim();

function humanize(testid) {
  // drop id fragments (uuid pieces like "a954" / "45dc", "user_3f2a9c...", bare numbers) so a row's button
  // reads "Delete staff", not "Delete staff a954 45dc"
  const isId = (w) => /^\d+$/.test(w) || /^[0-9a-f]{8,}$/i.test(w) || (/^[0-9a-f]{4,}$/i.test(w) && /\d/.test(w));
  const stripped = (testid || "").replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, "");  // whole uuids first
  const words = stripped.split(/[-_]/).filter((w) => w && !isId(w));
  if (!words.length) return "";
  const i = words.findIndex((w) => ACTIONS.includes(w.toLowerCase()));
  const ordered = i > 0 ? [words[i], ...words.slice(0, i), ...words.slice(i + 1)] : words;
  const s = ordered.join(" ").toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function linkLabels(root) {
  root.querySelectorAll("label:not([for])").forEach((label) => {
    if (label.querySelector(CONTROLS)) return;
    let ctl = label.nextElementSibling;
    if (ctl && !ctl.matches(CONTROLS)) ctl = ctl.querySelector?.(CONTROLS) || null;
    if (!ctl && label.parentElement) {
      const all = label.parentElement.querySelectorAll(CONTROLS);
      if (all.length === 1) ctl = all[0];
    }
    if (!ctl || (ctl.labels && ctl.labels.length)) return;
    if (!ctl.id) ctl.id = `a11y-${++uid}`;
    label.htmlFor = ctl.id;
  });
}

function nameControls(root) {
  root.querySelectorAll(CONTROLS).forEach((el) => {
    if (hasName(el) || el.getAttribute("placeholder")) return;
    let text = "";
    // nearest caption: previous sibling label/text, or the parent's label
    let p = el.previousElementSibling;
    if (p && /^(LABEL|P|SPAN|H\d)$/.test(p.tagName)) text = visibleText(p);
    if (!text) { const l = el.closest("label"); if (l) text = visibleText(l); }
    if (!text) text = humanize(el.getAttribute("data-testid"));
    if (text) el.setAttribute("aria-label", text.slice(0, 80));
  });
}

function nameButtons(root) {
  root.querySelectorAll("button, [role=button], a[href]").forEach((el) => {
    if (el.dataset.autoLabel) {
      if (visibleText(el)) { el.removeAttribute("aria-label"); delete el.dataset.autoLabel; }
      return;
    }
    if (hasName(el) || visibleText(el)) return;
    let name = humanize(el.getAttribute("data-testid"));
    // say which row it belongs to ("Delete staff - Aroha") when the row's first cell names it
    const row = el.closest("tr");
    const who = row ? visibleText(row.querySelector("td, th") || row).slice(0, 40) : "";
    if (name && who) name = `${name} - ${who}`;
    if (name) { el.setAttribute("aria-label", name); el.dataset.autoLabel = "1"; }
  });
}

export function installA11yHelpers() {
  if (typeof window === "undefined") return;
  let t = null;
  const run = () => { t = null; linkLabels(document.body); nameControls(document.body); nameButtons(document.body); };
  const schedule = () => { if (t === null) t = window.setTimeout(run, 250); };
  new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
  schedule();
}
